import { ReactElement } from "react";

import {
  Box,
  Hide,
  HStack,
  Show,
  Spacer,
  Stack,
  TabPanel,
  VStack,
} from "@chakra-ui/react";

import ExportButton from "components/ExportButton";
import { FiltersButtonWithModal } from "components/Filters";
import ResetAllFiltersButton from "components/ResetAllFiltersButton";
import SearchBoxWithIcon from "components/SearchBoxWithIcon";
import SortTermsSelect from "components/SortTermsSelect";
import { useRequestParams } from "contexts/RequestParamsProvider";
import { MOBILE_BREAKPOINT_SIZE } from "util/styleHelpers";
import { ComponentTab } from "./DocumentsPageTabs";

const TabPanelWithSearch = ({
  tabName,
  filters,
  component: Component,
  showSearch = true,
  sortOptions,
  exportObjectType,
}: ComponentTab): ReactElement => {
  const { searchTerm, onChangeSearchTerm, activeSort, onChangeSort } =
    useRequestParams();

  const displaySearchBoxAndDownloadButton = showSearch || exportObjectType;

  const exportButton = exportObjectType && (
    <ExportButton
      documentType={tabName?.toLowerCase()}
      objectType={exportObjectType}
    />
  );

  return (
    <TabPanel justifyContent="center" px={0}>
      <Stack spacing={4}>
        <Hide below={MOBILE_BREAKPOINT_SIZE}>
          {displaySearchBoxAndDownloadButton && (
            <HStack
              align={{ base: "flex-end", lg: "start", xl: "flex-end" }}
              justifyContent="space-between"
            >
              <HStack align="start" alignItems="flex-start" gap={3}>
                {showSearch && (
                  <Box>
                    <SearchBoxWithIcon
                      value={searchTerm}
                      onChange={onChangeSearchTerm}
                    />
                  </Box>
                )}
                <HStack flexWrap={{ base: "unset", lg: "wrap", xl: "nowrap" }}>
                  {filters.map(({ key, component: Component }) => (
                    <Component key={key} variant="button" />
                  ))}
                  <ResetAllFiltersButton />
                </HStack>
              </HStack>
              {exportButton}
            </HStack>
          )}
        </Hide>
        <Show below={MOBILE_BREAKPOINT_SIZE}>
          {displaySearchBoxAndDownloadButton && (
            <VStack>
              {showSearch && (
                <Box w="100%">
                  <SearchBoxWithIcon
                    value={searchTerm}
                    onChange={onChangeSearchTerm}
                  />
                </Box>
              )}
              <HStack w="100%" align="flex-end" spacing={4}>
                {filters.length > 0 && (
                  <FiltersButtonWithModal filters={filters} />
                )}
                <Box>
                  <SortTermsSelect
                    sortOptions={sortOptions}
                    selectedSortKey={activeSort?.key}
                    selectedDirection={activeSort?.direction}
                    onChange={onChangeSort}
                  />
                </Box>
                <Spacer />
                {exportButton}
              </HStack>
            </VStack>
          )}
        </Show>
        <Box px={2} overflowX="auto">
          <Component search={searchTerm} />
        </Box>
      </Stack>
    </TabPanel>
  );
};

export default TabPanelWithSearch;
